import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { SecurityService } from '../../services/security.service';
import { LoaderService } from '../../services/loader.service';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, ValidatorFn } from '@angular/forms';
import { ConfigInput, ConfigSelected } from '../../interfaces/interfaces';
import { CommonModule } from '@angular/common';
import { InputComponent } from '../../shared/input/input.component';
import { SelectedComponent } from '../../shared/selected/selected.component';
import { LockToggleComponent } from '../../shared/lock-toggle/lock-toggle.component';
import { COUNTRIES } from '../../config/constants.config';
import { RouteService } from '../../services/route.service';
import { HelpService } from '../../services/help.service';
import { OtpSessionService } from '../../services/otp-session.service';
import { maxTodayColombiaValidator } from 'app/config/functions.shared';
import { Subscription } from 'rxjs';
import { toast } from 'ngx-sonner';
import { buildPreloadedConfig, buildPreloadedSelectConfig, coerceBooleanValue, normalizeSelectValue, resolveOptions } from '../../shared/utils/field-preload.utils';
import { ListsService } from 'app/services/lists.service';
import { FieldErrorTimeoutService } from 'app/services/field-error-timeout.service';
import { debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import {
  buildForeignSavePayload,
  coerceForeignBoolean,
  isForeignFormComplete,
  isLockedDefaultFalseField,
} from './foreign-form.utils';

interface Country {
  id: number;
  name: string;
  code: string;
}

interface TinFormat {
  id: number;
  regular_expression: string;
  description?: string;
}

@Component({
  selector: 'app-foreign',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectedComponent,
    InputComponent,
    LockToggleComponent
  ],
  templateUrl: './foreign.component.html',
  styleUrl: './foreign.component.css'
})
export class ForeignComponent implements OnInit, OnDestroy, OnChanges {

  form: FormGroup;
  inputCountryValue: string = '';
  filteredCountries: Country[] = [];
  isTimeout = false;
  countries: Country[] = [];
  selectedCountryFormats: TinFormat[] = [];
  isLoadingFormats = false;
  isLoadingCountries = false;
  countryUnlocked = signal(true);
  tinUnlocked = signal(true);
  nextRoute: WritableSignal<string> = signal('');
  private searchSubject = new Subject<string>();
  private selectedCountry: Country | null = null;

  @Output() validData = new EventEmitter<boolean>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;

  private lastForeignResponse: any = null;
  private formSubscription: Subscription = new Subscription();
  private countryValidationSubscription: Subscription = new Subscription();
  private tinValidationSubscription: Subscription = new Subscription();
  private countryTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  dataSelected: WritableSignal<ConfigSelected[]> = signal([
    { label: '¿Administras recursos públicos o ejerces un cargo importante?', subLabel: '', formName: 'hasPublicJobManager', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{ value: true, label: 'Si' }, { value: false, label: 'No' }]), showBlocker: true },
    { label: '¿Eres familiar de un PEP? (cónyuge o compañero permanente, hijo, padre, hermano, nieto, abuelo, suegro o cuñado)', subLabel: '', formName: 'isPepRelative', placeholder: '', type: 'text', defaultValue: false, enabled: false, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{ value: true, label: 'Si' }, { value: false, label: 'No' }]), showBlocker: true },
    { label: '¿Tienes Green Card?', subLabel: '', formName: 'hasGreenCard', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{ value: true, label: 'Si' }, { value: false, label: 'No' }]), showBlocker: true },
    { label: '¿Tus padres tienen nacionalidad norteamericana?', subLabel: '', formName: 'hasNothAmericanNationality', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{ value: true, label: 'Si' }, { value: false, label: 'No' }]), showBlocker: true },
    { label: '¿Está obligado a tributar en un país diferente a Colombia?', subLabel: '', formName: 'isObligatedTaxOtherCountry', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{ value: true, label: 'Si' }, { value: false, label: 'No' }]), showBlocker: true },
    { label: '¿Es extranjero residente?', subLabel: '', formName: 'isForeignResident', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{ value: true, label: 'Si' }, { value: false, label: 'No' }]), showBlocker: true },
    { label: '¿Ha estado más de 183 días en el último año, o 183 días en los últimos 3 años, en Estados Unidos o fuera del país?', subLabel: '', formName: 'hasStayedAbroad183Days', placeholder: '', type: 'text', defaultValue: false, enabled: false, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{ value: true, label: 'Si' }, { value: false, label: 'No' }]), showBlocker: true },
  ]);

  pepDocumentTypeSelected: WritableSignal<ConfigSelected> = signal({
    label: 'Tipo de documento',
    subLabel: '',
    formName: 'pepRelativeDocumentType',
    placeholder: '',
    type: 'text',
    defaultValue: '',
    enabled: false,
    validation: [Validators.required],
    textError: 'Por favor selecciona un tipo de documento',
    showBlocker: true,
    options: signal([
      { value: 1, label: 'Cédula de ciudadanía' },
      { value: 2, label: 'Cédula de extranjería' },
      { value: 48, label: 'PPT' },
      { value: 5, label: 'Pasaporte' },
    ]),
  });

  publicJobInputs: WritableSignal<ConfigInput[]> = signal([
    { label: 'Cargo', subLabel: '*', formName: 'publicJobPosition', placeholder: 'Ingresa el cargo', type: 'text', defaultValue: '', enabled: false, validation: [Validators.required, Validators.minLength(3)], textError: 'El cargo debe tener mínimo 3 caracteres.', showBlocker: true },
    { label: 'Fecha de ingreso al cargo', subLabel: '*', formName: 'publicJobStartDate', placeholder: '', type: 'date', defaultValue: '', enabled: false, validation: [Validators.required, maxTodayColombiaValidator], textError: 'Selecciona una fecha de ingreso válida.', showBlocker: true },
    { label: 'Fecha de retiro del cargo (cuando aplique)', subLabel: '', formName: 'publicJobEndDate', placeholder: '', type: 'date', defaultValue: '', enabled: false, validation: [maxTodayColombiaValidator], textError: 'La fecha de retiro no puede ser futura.', showBlocker: true },
  ]);

  pepRelativeInputs: WritableSignal<ConfigInput[]> = signal([
    { label: 'Nombres y apellidos', subLabel: '*', formName: 'pepRelativeFullName', placeholder: 'Ingresa nombres y apellidos', type: 'text', defaultValue: '', enabled: false, validation: [Validators.required, Validators.minLength(7)], textError: 'Ingresa nombres y apellidos con mínimo 7 caracteres.', showBlocker: true },
    { label: 'Número de documento', subLabel: '*', formName: 'pepRelativeDocumentNumber', placeholder: 'Ingresa el número de documento', type: 'text', defaultValue: '', enabled: false, validation: [Validators.required, Validators.minLength(6), Validators.maxLength(10), Validators.pattern(/^[a-zA-Z0-9]+$/)], textError: 'Ingresa un número de documento alfanumérico de 6 a 10 caracteres.', showBlocker: true },
    { label: 'Cargo / Entidad del PEP', subLabel: '*', formName: 'pepRelativePositionEntity', placeholder: 'Ingresa el cargo o entidad', type: 'text', defaultValue: '', enabled: false, validation: [Validators.required, Validators.minLength(3)], textError: 'Ingresa el cargo o entidad del PEP con mínimo 3 caracteres.', showBlocker: true },
  ]);

  constructor(
    private _router: Router,
    private _title: Title,
    private _securityService: SecurityService,
    private _listService: ListsService,
    private _loaderService: LoaderService,
    private _routeService: RouteService,
    private _helpService: HelpService,
    private _otpSessionService: OtpSessionService,
    private _cdr: ChangeDetectorRef,
    private fieldErrorTimeout: FieldErrorTimeoutService
  ) {
    this._title.setTitle('Onboarding Digital | Información Adicional');
    this.form = new FormGroup({
      hasPublicJobManager: new FormControl(null, [Validators.required]),
      isPepRelative: new FormControl(null, [Validators.required]),
      publicJobPosition: new FormControl(null),
      publicJobStartDate: new FormControl(null),
      publicJobEndDate: new FormControl(null),
      pepRelativeFullName: new FormControl(null),
      pepRelativeDocumentType: new FormControl(null),
      pepRelativeDocumentNumber: new FormControl(null),
      pepRelativePositionEntity: new FormControl(null),
      hasGreenCard: new FormControl(null, [Validators.required]),
      hasNothAmericanNationality: new FormControl(null, [Validators.required]),
      isObligatedTaxOtherCountry: new FormControl(null, [Validators.required]),
      obligatedTaxCountry: new FormControl(null),
      obligatedTaxCountryId: new FormControl(null),
      tinNumber: new FormControl(null),
      isForeignResident: new FormControl(null, [Validators.required]),
      hasStayedAbroad183Days: new FormControl(null, [Validators.required])
    }, [this.countryValidator.bind(this), this.tinValidator.bind(this)]);

    this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');

    // Suscripción para cuando cambia la obligación tributaria
    this.form.get('isObligatedTaxOtherCountry')?.valueChanges.subscribe((value: boolean) => {
      this.toggleCountryValidation(value);
      this.toggleTinValidation(value);
      if (value === true) {
        this.scheduleCountryFieldTimeout();
      }
    });

    this.form.get('hasPublicJobManager')?.valueChanges.subscribe((value) => {
      this.togglePublicJobValidation(value);
    });

    this.form.get('isPepRelative')?.valueChanges.subscribe((value) => {
      this.togglePepRelativeValidation(value);
    });

    // Suscripción para cuando cambia el país seleccionado
    this.form.get('obligatedTaxCountryId')?.valueChanges.subscribe((countryId: number) => {
      if (countryId) {
        this.loadCountryFormats(countryId);
      } else {
        this.selectedCountryFormats = [];
        this.selectedCountry = null;
        this.form.get('tinNumber')?.setValue('');
        this._cdr.detectChanges();
      }
      this.updateTinValidation();
      this.emitValidationState();
    });

    // Configuración del buscador de países
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((searchTerm: string) => {
        if (searchTerm && searchTerm.length >= 2) {
          this.isLoadingCountries = true;
          this._cdr.detectChanges();
          return this._listService.searchCountries(searchTerm).pipe(
            finalize(() => {
              this.isLoadingCountries = false;
              this._cdr.detectChanges();
            })
          );
        } else {
          this.filteredCountries = [];
          this.isLoadingCountries = false;
          this._cdr.detectChanges();
          return of({ countries: [] });
        }
      })
    ).subscribe({
      next: (response: any) => {
        this.filteredCountries = response.countries.map((c: any) => ({
          id: c.id,
          name: c.name,
          code: c.code
        }));
        this._cdr.detectChanges();
      },
      error: (error: any) => {
        console.error("ERROR buscando países: ", error);
        this.filteredCountries = [];
        this.isLoadingCountries = false;
        this._cdr.detectChanges();
        toast.error('Error al buscar países.');
      }
    });

    this.syncDisplayedBooleanDefaults();
  }

  async ngOnInit() {
    if (this.isCheckData) {
      this._loaderService.hide();
      if (this.dataLastOnboarding) {
        this.applyForeignResponse(this.dataLastOnboarding);
      } else {
        this.syncDisplayedBooleanDefaults();
      }
      this.setupSubscriptions();
      return;
    }

    const authRoute = localStorage.getItem('route');
    if (!authRoute) {
      this._router.navigateByUrl('');
      return;
    }
    const currentUrl = this._router.url;
    if (authRoute && currentUrl !== authRoute) {
      await this._router.navigateByUrl(authRoute);
    } else {
      this._loaderService.hide();
      this._helpService.show();
      this.getForeign();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isCheckData) {
      return;
    }
    if (changes['dataLastOnboarding']?.currentValue) {
      this.applyForeignResponse(changes['dataLastOnboarding'].currentValue);
      this.setupSubscriptions();
    }
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
    this.countryValidationSubscription.unsubscribe();
    this.tinValidationSubscription.unsubscribe();
    this.searchSubject.complete();
    if (this.countryTimeoutHandle) {
      clearTimeout(this.countryTimeoutHandle);
    }
  }

  private scheduleCountryFieldTimeout(): void {
    if (this.isCheckData) {
      return;
    }
    if (this.countryTimeoutHandle) {
      clearTimeout(this.countryTimeoutHandle);
    }
    this.isTimeout = false;
    this.countryTimeoutHandle = setTimeout(() => {
      this.isTimeout = true;
      this._cdr.detectChanges();
    }, this.fieldErrorTimeout.delayMs(8));
  }

  canContinue(): boolean {
    return this.isFormCompletelyValid();
  }

  private isFormCompletelyValid(): boolean {
    const values = this.form.getRawValue();
    const taxObligation = coerceForeignBoolean(values['isObligatedTaxOtherCountry']) === true;

    return isForeignFormComplete(values, {
      isValidCountry: taxObligation ? this.isValidCountryId(values['obligatedTaxCountryId']) : true,
      tinFormats: this.selectedCountryFormats,
    });
  }

  private emitValidationState(): void {
    if (!this.isCheckData) return;
    this.validData.emit(this.isFormCompletelyValid());
  }

  private setupSubscriptions(): void {
    if (!this.isCheckData) return;

    this.formSubscription.unsubscribe();
    this.countryValidationSubscription.unsubscribe();

    this.formSubscription = this.form.valueChanges.subscribe(() => {
      this.emitValidationState();
    });

    this.countryValidationSubscription = this.form.get('isObligatedTaxOtherCountry')!.valueChanges.subscribe((value: boolean) => {
      this.toggleCountryValidation(value);
      this.toggleTinValidation(value);
      setTimeout(() => this.emitValidationState(), 100);
    });

    setTimeout(() => this.emitValidationState(), 500);
  }

  shouldShowCountryField(): boolean {
    const current = coerceForeignBoolean(this.form.get('isObligatedTaxOtherCountry')?.value);
    if (current !== null) {
      return current;
    }

    return this.isCheckData && !!this.dataLastOnboarding?.obligatedTaxCountry;
  }

  shouldShowTinField(): boolean {
    const current = coerceForeignBoolean(this.form.get('isObligatedTaxOtherCountry')?.value);
    if (current !== null) {
      return current;
    }

    return this.isCheckData && !!(this.dataLastOnboarding?.tinNumber || this.dataLastOnboarding?.tin);
  }

  get showPublicJobFields(): boolean {
    const current = coerceForeignBoolean(this.form.get('hasPublicJobManager')?.value);
    if (current !== null) {
      return current;
    }

    return this.isCheckData && coerceForeignBoolean(this.dataLastOnboarding?.hasPublicJobManager) === true;
  }

  get showPepRelativeFields(): boolean {
    const current = coerceForeignBoolean(this.form.get('isPepRelative')?.value);
    if (current !== null) {
      return current;
    }

    return this.isCheckData && coerceForeignBoolean(this.dataLastOnboarding?.isPepRelative) === true;
  }

  loadCountryFormats(countryId: number) {
    if (!countryId) {
      this.selectedCountryFormats = [];
      this._cdr.detectChanges();
      return;
    }

    this.isLoadingFormats = true;
    this._cdr.detectChanges();

    this._listService.getCountryFormats(countryId)
      .pipe(
        finalize(() => {
          this.isLoadingFormats = false;
          this._cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          this.selectedCountryFormats = response.formats || [];
          this.updateTinValidation();
          this._cdr.detectChanges();
          this.emitValidationState();
        },
        error: (error: any) => {
          console.error("ERROR cargando formatos: ", error);
          this.selectedCountryFormats = [];
          toast.error('No se pudieron cargar los formatos TIN del país seleccionado.');
          this._cdr.detectChanges();
        }
      });
  }

  getForeign() {
    this._securityService.getForeign().subscribe({
      next: (response: any) => {
        this.applyForeignResponse(response);
        this.setupSubscriptions();
        this.emitValidationState();
      },
      error: (error: any) => {
        console.error('ERROR: ', error);
        toast.error('No se pudo cargar la información guardada.');
      }
    });
  }

  private applyForeignResponse(response: any) {
    if (!response) {
      return;
    }

    this.lastForeignResponse = response;

    const normalizedResponse = { ...response };

    this.dataSelected().forEach((select: any) => {
      const options = resolveOptions(select.options);
      let normalized = normalizeSelectValue(
        response[select.formName],
        options
      );

      const isLockedDefaultFalse = isLockedDefaultFalseField(select.formName);
      if (isLockedDefaultFalse && coerceForeignBoolean(normalized) === null) {
        normalized = false;
      }

      normalizedResponse[select.formName] = normalized;
    });

    this.form.patchValue(normalizedResponse, { emitEvent: false });

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) => {
        const preloaded = buildPreloadedSelectConfig(select, normalizedResponse[select.formName], select.options);
        const isAddedField = isLockedDefaultFalseField(select.formName);
        if (!isAddedField) {
          return preloaded;
        }

        const hasBooleanValue = preloaded.defaultValue === true || preloaded.defaultValue === false;
        return {
          ...preloaded,
          defaultValue: hasBooleanValue ? preloaded.defaultValue : false,
          enabled: false,
          showBlocker: true,
        };
      })
    );

    this.syncDisplayedBooleanDefaults();

    this.refreshConditionalFieldConfigs(response);

    const savedTin = response.tinNumber ?? response.tin;
    if (savedTin) {
      this.form.get('tinNumber')?.setValue(savedTin, { emitEvent: false });
    }

    this.togglePublicJobValidation(normalizedResponse.hasPublicJobManager);
    this.togglePepRelativeValidation(normalizedResponse.isPepRelative);
    this.toggleCountryValidation(normalizedResponse.isObligatedTaxOtherCountry);
    this.toggleTinValidation(normalizedResponse.isObligatedTaxOtherCountry);

    const countryId = response.obligatedTaxCountryId;
    const countryCode = response.obligatedTaxCountry;

    if (countryCode) {
      this.handleCountryByCode(countryCode);
    } else if (countryId) {
      this.form.get('obligatedTaxCountryId')?.setValue(countryId, { emitEvent: false });
      this.form.get('obligatedTaxCountry')?.setValue(countryCode, { emitEvent: false });
      this.loadCountryFormats(countryId);
      this.findAndSetCountryName(countryId);
    }

    this.emitValidationState();
    this._cdr.detectChanges();
  }

  private syncDisplayedBooleanDefaults(): void {
    for (const select of this.dataSelected()) {
      const displayed = coerceForeignBoolean(select.defaultValue);
      if (displayed === null || !select.formName) {
        continue;
      }

      const control = this.form.get(select.formName);
      if (!control) {
        continue;
      }

      if (coerceForeignBoolean(control.value) === null) {
        control.setValue(displayed, { emitEvent: true });
      }
    }
  }

  private refreshConditionalFieldConfigs(source: any = this.lastForeignResponse ?? this.dataLastOnboarding) {
    if (!source) {
      return;
    }

    this.publicJobInputs.update((inputs: any) =>
      inputs.map((input: any) => ({
        ...buildPreloadedConfig(input, source[input.formName], input.type),
        enabled: false,
        showBlocker: true,
      }))
    );

    this.pepRelativeInputs.update((inputs: any) =>
      inputs.map((input: any) => ({
        ...buildPreloadedConfig(input, source[input.formName], input.type),
        enabled: false,
        showBlocker: true,
      }))
    );

    this.pepDocumentTypeSelected.update((config: any) => ({
      ...buildPreloadedSelectConfig(config, source.pepRelativeDocumentType, config.options, source),
      enabled: false,
      showBlocker: true,
    }));
  }

  private findAndSetCountryName(countryId: number) {
    // Buscar en filteredCountries o hacer una búsqueda
    const country = this.filteredCountries.find(c => c.id === countryId);
    if (country) {
      this.inputCountryValue = country.name;
      this.selectedCountry = country;
    } else {
      // Si no está en filteredCountries, hacer búsqueda por ID
      this._listService.searchCountries('').subscribe({
        next: (response: any) => {
          const found = response.countries.find((c: any) => c.id === countryId);
          if (found) {
            this.inputCountryValue = found.name;
            this.selectedCountry = found;
          }
        }
      });
    }
  }

  private handleCountryByCode(countryCode: string) {
    if (countryCode) {
      this._listService.searchCountries(countryCode).subscribe({
        next: (response: any) => {
          const country = response.countries.find((c: any) =>
            String(c.code) === String(countryCode)
            || String(c.id) === String(countryCode)
            || (c.name && c.name.toUpperCase() === String(countryCode).toUpperCase())
          );
          if (country) {
            this.selectedCountry = country;
            this.form.get('obligatedTaxCountryId')?.setValue(country.id, { emitEvent: false });
            this.form.get('obligatedTaxCountry')?.setValue(country.code, { emitEvent: false });
            this.inputCountryValue = country.name;
            this.loadCountryFormats(country.id);
          }
        },
        error: () => {}
      });
    }
  }

  toggleCountryValidation(isObligated: any) {
    const countryControl = this.form.get('obligatedTaxCountryId');
    const boolValue = coerceBooleanValue(isObligated) === true;

    if (boolValue) {
      countryControl?.setValidators([Validators.required]);
    } else {
      countryControl?.clearValidators();
      if (!this.isCheckData) {
        countryControl?.setValue(null);
        this.form.get('obligatedTaxCountry')?.setValue(null);
        this.inputCountryValue = '';
        this.selectedCountryFormats = [];
        this.filteredCountries = [];
        this.selectedCountry = null;
        this.form.get('tinNumber')?.setValue('');
      }
    }

    countryControl?.updateValueAndValidity({ emitEvent: false });
  }

  toggleTinValidation(isObligated: any) {
    const tinControl = this.form.get('tinNumber');
    const boolValue = coerceBooleanValue(isObligated) === true;

    if (boolValue) {
      tinControl?.setValidators([Validators.required, this.tinFormatValidator()]);
    } else {
      tinControl?.clearValidators();
      if (!this.isCheckData) {
        tinControl?.setValue('');
        this.selectedCountryFormats = [];
      }
    }

    tinControl?.updateValueAndValidity({ emitEvent: false });
  }

  tinFormatValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const countryId = this.form?.get('obligatedTaxCountryId')?.value;
      if (!countryId) return null;

      if (this.selectedCountryFormats.length === 0) {
        return null;
      }

      const tin = control.value;

      const validFormat = this.selectedCountryFormats.some(format => {
        try {
          const regex = new RegExp(format.regular_expression);
          return regex.test(tin);
        } catch (e) {
          return false;
        }
      });

      if (!validFormat) {
        const formatExamples = this.selectedCountryFormats
          .map(f => f.description || f.regular_expression)
          .join(' • ');

        return {
          invalidTinFormat: {
            message: `Formato TIN inválido. Formatos aceptados: ${formatExamples}`
          }
        };
      }

      return null;
    };
  }

  tinValidator(control: AbstractControl) {
    const form = control as FormGroup;
    const isObligated = form.get('isObligatedTaxOtherCountry')?.value;
    const tin = form.get('tinNumber')?.value;

    if (coerceBooleanValue(isObligated) === true && !tin) {
      return { tinRequired: true };
    }
    return null;
  }

  updateTinValidation() {
    const tinControl = this.form.get('tinNumber');
    if (tinControl?.hasValidator(Validators.required)) {
      tinControl.updateValueAndValidity();
    }
  }

  togglePublicJobValidation(isPublicJob: any) {
    const positionControl = this.form.get('publicJobPosition');
    const startDateControl = this.form.get('publicJobStartDate');
    const endDateControl = this.form.get('publicJobEndDate');

    if (coerceBooleanValue(isPublicJob) === true) {
      positionControl?.setValidators([Validators.required, Validators.minLength(3)]);
      startDateControl?.setValidators([Validators.required, maxTodayColombiaValidator]);
      endDateControl?.setValidators([maxTodayColombiaValidator]);
    } else {
      positionControl?.clearValidators();
      startDateControl?.clearValidators();
      endDateControl?.clearValidators();
      if (!this.isCheckData) {
        positionControl?.setValue(null);
        startDateControl?.setValue(null);
        endDateControl?.setValue(null);
      }
    }

    positionControl?.updateValueAndValidity({ emitEvent: false });
    startDateControl?.updateValueAndValidity({ emitEvent: false });
    endDateControl?.updateValueAndValidity({ emitEvent: false });
  }

  togglePepRelativeValidation(isPepRelative: any) {
    const fullNameControl = this.form.get('pepRelativeFullName');
    const documentTypeControl = this.form.get('pepRelativeDocumentType');
    const documentNumberControl = this.form.get('pepRelativeDocumentNumber');
    const positionEntityControl = this.form.get('pepRelativePositionEntity');

    if (coerceBooleanValue(isPepRelative) === true) {
      fullNameControl?.setValidators([Validators.required, Validators.minLength(7)]);
      documentTypeControl?.setValidators([Validators.required]);
      documentNumberControl?.setValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(10),
        Validators.pattern(/^[a-zA-Z0-9]+$/),
      ]);
      positionEntityControl?.setValidators([Validators.required, Validators.minLength(3)]);
    } else {
      fullNameControl?.clearValidators();
      documentTypeControl?.clearValidators();
      documentNumberControl?.clearValidators();
      positionEntityControl?.clearValidators();
      if (!this.isCheckData) {
        fullNameControl?.setValue(null);
        documentTypeControl?.setValue(null);
        documentNumberControl?.setValue(null);
        positionEntityControl?.setValue(null);
      }
    }

    fullNameControl?.updateValueAndValidity({ emitEvent: false });
    documentTypeControl?.updateValueAndValidity({ emitEvent: false });
    documentNumberControl?.updateValueAndValidity({ emitEvent: false });
    positionEntityControl?.updateValueAndValidity({ emitEvent: false });
  }

  onSubmit(): Promise<void> | void {
    if (this.isCheckData) {
      return this.saveForeignData();
    }

    if (!this.canContinue()) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    this._loaderService.show('Enviando código de verificación...');

    return new Promise((resolve, reject) => {
      this._securityService.setForeign(this.buildPayload()).subscribe({
        next: () => {
          this.sendOtpAndContinue().then(resolve).catch(reject);
        },
        error: (error: any) => {
          this._loaderService.hide();
          console.error('ERROR: ', error);
          toast.error('No pudimos guardar tu información. Intenta de nuevo.');
          reject(error);
        }
      });
    });
  }

  private buildPayload(): any {
    return buildForeignSavePayload(
      this.form.getRawValue(),
      this.getCountryCode(this.form.get('obligatedTaxCountryId')?.value)
        ?? this.form.get('obligatedTaxCountry')?.value
        ?? this.selectedCountry?.code
        ?? null
    );
  }

  private getCountryCode(countryId: number): string | null {
    if (this.selectedCountry && this.selectedCountry.id === countryId) {
      return this.selectedCountry.code;
    }
    const country = this.filteredCountries.find(c => c.id === countryId);
    return country ? country.code : null;
  }

  saveForeignData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._securityService.setForeign(this.buildPayload()).subscribe({
        next: () => resolve(),
        error: (error: any) => {
          console.error('ERROR: ', error);
          reject(error);
        }
      });
    });
  }

  private sendOtpAndContinue(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._securityService.sendOTP().subscribe({
        next: (response: any) => {
          this._loaderService.hide();

          if (response.checkpoint === 'SIGNED_11') {
            this._otpSessionService.clearOtpContext();
            const finishRoute = this._routeService.getNextRoute(this.nextRoute()) ?? '/app/finish';
            localStorage.setItem('route', finishRoute);
            this._router.navigateByUrl(finishRoute);
            resolve();
            return;
          }

          this._otpSessionService.saveOtpContext(response);
          localStorage.setItem('route', this.nextRoute());
          this._router.navigateByUrl(this.nextRoute());
          resolve();
        },
        error: (error: any) => {
          this._loaderService.hide();
          console.error('ERROR sendOTP: ', error);
          toast.error('No pudimos enviar el código de verificación. Verifica tu conexión e intenta de nuevo.');
          reject(error);
        }
      });
    });
  }

  handleValue(event: { formName: string, value: boolean | string | number }) {
    this.form.get(event.formName)?.setValue(event.value);
    if (event.formName === 'isObligatedTaxOtherCountry') {
      this.toggleCountryValidation(event.value);
      this.toggleTinValidation(event.value);
    }
    if (event.formName === 'hasPublicJobManager') {
      this.togglePublicJobValidation(event.value);
      if (coerceBooleanValue(event.value) === true) {
        this.refreshConditionalFieldConfigs();
      }
    }
    if (event.formName === 'isPepRelative') {
      this.togglePepRelativeValidation(event.value);
      if (coerceBooleanValue(event.value) === true) {
        this.refreshConditionalFieldConfigs();
      }
    }
    this.emitValidationState();
  }

  handleInputValue(event: { formName: string, value: string | number }) {
    this.form.get(event.formName)?.setValue(event.value);
    this.form.get(event.formName)?.markAsTouched();
    this.emitValidationState();
  }

  onSearchChange(value: string) {
    this.inputCountryValue = value;

    if (this.selectedCountry && value !== this.selectedCountry.name) {
      this.selectedCountry = null;
      this.form.get('obligatedTaxCountryId')?.setValue(null);
      this.form.get('obligatedTaxCountry')?.setValue(null);
      this.selectedCountryFormats = [];
      this.form.get('tinNumber')?.setValue('');
      this.form.get('obligatedTaxCountryId')?.markAsTouched();
      this.form.updateValueAndValidity();
    }

    this.searchSubject.next(value);
  }

  selectCountry(country: Country) {
    const previousCountryId = this.selectedCountry?.id;
    this.selectedCountry = country;
    this.form.get('obligatedTaxCountryId')?.setValue(country.id);
    this.form.get('obligatedTaxCountry')?.setValue(country.code);
    this.inputCountryValue = country.name;
    this.filteredCountries = [];
    if (previousCountryId !== country.id) {
      this.form.get('tinNumber')?.setValue('');
    }
    this.form.get('obligatedTaxCountryId')?.markAsTouched();
    this.loadCountryFormats(country.id);

    this.form.updateValueAndValidity();
    this._cdr.detectChanges();
    this.emitValidationState();
  }

  toggleCountryLock(): void {
    this.countryUnlocked.update((value) => !value);
  }

  toggleTinLock(): void {
    this.tinUnlocked.update((value) => !value);
  }

  countryValidator(control: AbstractControl) {
    const form = control as FormGroup;
    const taxObligation = form.get('isObligatedTaxOtherCountry')?.value;
    const countryId = form.get('obligatedTaxCountryId')?.value;

    if (coerceBooleanValue(taxObligation) === true) {
      const exists = (this.selectedCountry && this.selectedCountry.id === countryId) ||
                     this.filteredCountries.some(c => c.id === countryId);
      if (!countryId || !exists) {
        return { invalidCountry: true };
      }
    }
    return null;
  }

  private isValidCountryId(countryId: number): boolean {
    if (this.selectedCountry && this.selectedCountry.id === countryId) {
      return true;
    }
    return this.filteredCountries.some(c => c.id === countryId);
  }

  private generateExampleFromRegex(regex: string): string {
    try {
      let example = regex
        .replace(/[\[\]\(\)\{\}]/g, '')
        .replace(/\\d/g, '9')
        .replace(/[a-zA-Z]/g, 'X')
        .replace(/\^/g, '')
        .replace(/\$/g, '')
        .replace(/\+/g, '')
        .replace(/\*/g, '')
        .replace(/\?/g, '')
        .replace(/\|/g, '/')
        .replace(/\\/g, '');

      if (example.length > 15) {
        example = example.substring(0, 15);
      }
      return example;
    } catch {
      return regex;
    }
  }

  getTinPlaceholder(): string {
    if (this.isLoadingFormats) {
      return 'Cargando formatos...';
    }

    if (this.selectedCountryFormats.length === 0) {
      return 'Selecciona un país primero';
    }

    const examples = this.selectedCountryFormats
      .map(f => this.generateExampleFromRegex(f.regular_expression))
      .filter(ex => ex && ex.length > 0);

    if (examples.length === 1) {
      return `Ej: ${examples[0]}`;
    } else if (examples.length > 1) {
      return `Ej: ${examples.join(' o ')}`;
    }

    return 'Ingresa tu número TIN';
  }

  getPepDocumentTypeLabel(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const option = this.pepDocumentTypeSelected().options().find(
      (item) => String(item.value) === String(value)
    );
    return option?.label ?? String(value);
  }
}
